<template>
  <section ref="sectionRef" id="works" class="works" aria-labelledby="works-title">
    <div class="works__container">
      <!-- Progress Navigation -->
      <nav class="works__progress" aria-label="Work items navigation">
        <template v-for="index in totalItems" :key="index - 1">
          <button
            v-if="true"
            type="button"
            class="works__progress-dot"
            :class="{
              'works__progress-dot--active': activeIndex === index - 1,
              'works__progress-dot--last':
                index - 1 === inProgressDividerIndex ||
                index === 1 ||
                index - 1 === otherDividerIndex ||
                index - 1 === closingIndex,
              'works__progress-dot--building':
                index - 1 >= inProgressDividerIndex && index !== totalItems,
            }"
            :style="getDotArcStyle(index - 1)"
            :aria-label="`Go to project ${index - 1}`"
            :aria-current="activeIndex === index - 1 ? 'step' : undefined"
            @click="navigateToItem(index - 1)"
          />
        </template>
      </nav>

      <!-- 3D Cylinder -->
      <div ref="cylinderWrapperRef" class="works__cylinder-wrapper">
        <div class="works__cylinder">
          <!-- Header Item (distinct style) -->
          <header
            class="works__cylinder-item works__cylinder-item--header"
            :class="{ 'works__cylinder-item--active': activeIndex === 0 }"
            :style="getItemStyle(0)"
          >
            <div class="works__header-content">
              <h2 id="works-title" class="works__title">
                <span class="works__title-line">{{ $t('page.works.section.built') }}</span>
              </h2>
              <div class="works__scroll-hint" aria-hidden="true">
                <span class="works__scroll-hint-icon">↓</span>
              </div>
            </div>
          </header>

          <!-- Work Items -->
          <article
            v-for="(project, index) in mergedWorksData"
            :key="project.id"
            class="works__cylinder-item"
            :class="{
              'works__cylinder-item--has-tab': !project.noCaseStudy,
              'works__cylinder-item--active': isItemActive(index),
            }"
            :style="getItemStyle(index + 1)"
            :aria-selected="isItemActive(index)"
            @click="handleItemClick(project, index)"
          >
            <div v-if="!project.noCaseStudy" class="works__case-study-tab">
              <span class="works__case-study-tab-text">{{ $t('page.works.ui.caseStudy') }}</span>
              <span
                class="works__case-study-tab-loader"
                :aria-label="$t('page.works.ui.comingSoon')"
                :data-tooltip="$t('page.works.ui.comingSoon')"
              >
                <span class="pixel"></span>
                <span class="pixel"></span>
                <span class="pixel"></span>
                <span class="pixel"></span>
                <span class="pixel"></span>
                <span class="pixel"></span>
              </span>
            </div>
            <div class="works__cylinder-item-inner">
              <figure class="works__app-icon">
                <img
                  :src="project.icon || getPlaceholderIcon(project.id)"
                  :alt="`${project.title} icon`"
                  class="works__app-icon-img"
                  loading="lazy"
                />
              </figure>
              <div class="works__item-content">
                <div class="works__item-header">
                  <h3 class="works__cylinder-title">{{ project.title }}</h3>
                </div>
                <span class="works__cylinder-category">{{ project.category }}</span>
              </div>
              <div v-if="project.link || project.showModal" class="works__other-actions">
                <button
                  v-if="project.showModal"
                  class="works__other-info-btn"
                  :aria-label="`More info about ${project.title}`"
                  @click.stop="
                    openModal({
                      name: project.title,
                      role: project.role || project.category,
                      duration: project.year,
                      description: project.description,
                      tech: project.tech,
                      url: project.link,
                      logo: project.icon,
                      image: project.src || project.image || null,
                    })
                  "
                >
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" stroke-width="1.2" />
                    <path
                      d="M6.5 5.5v4M6.5 4h.01"
                      stroke="currentColor"
                      stroke-width="1.2"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
                <a
                  v-if="project.link"
                  :href="project.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="works__other-link"
                  :aria-label="`Visit ${project.title}`"
                  >↗</a
                >
              </div>
            </div>
          </article>
          <!-- Building Section Divider -->
          <div
            class="works__cylinder-item works__cylinder-item--building-header"
            :class="{ 'works__cylinder-item--active': activeIndex === inProgressDividerIndex }"
            :style="getItemStyle(inProgressDividerIndex)"
            aria-label="Currently building section"
          >
            <div class="works__building-header-content">
              <h2 class="works__title">
                <span class="works__title-line">{{ $t('page.works.section.building') }}</span>
              </h2>
            </div>
          </div>

          <!-- In Progress Items -->
          <article
            v-for="(project, index) in mergedInProgressData"
            :key="`wip-${project.id}`"
            class="works__cylinder-item works__cylinder-item--has-tab"
            :class="{ 'works__cylinder-item--active': isInProgressActive(index) }"
            :style="getItemStyle(inProgressDividerIndex + 1 + index)"
            @click="handleInProgressClick(project, index)"
          >
            <div class="works__case-study-tab">
              <span class="works__case-study-tab-text">{{ $t('page.works.ui.caseStudy') }}</span>
              <span
                class="works__case-study-tab-loader"
                :aria-label="$t('page.works.ui.comingSoon')"
                :data-tooltip="$t('page.works.ui.comingSoon')"
              >
                <span class="pixel"></span>
                <span class="pixel"></span>
                <span class="pixel"></span>
                <span class="pixel"></span>
                <span class="pixel"></span>
                <span class="pixel"></span>
              </span>
            </div>
            <div class="works__cylinder-item-inner">
              <figure class="works__app-icon">
                <img
                  :src="project.icon || getPlaceholderIcon(project.id)"
                  :alt="`${project.title} icon`"
                  class="works__app-icon-img"
                  loading="lazy"
                />
              </figure>
              <div class="works__item-content">
                <div class="works__item-header">
                  <h3 class="works__cylinder-title">{{ project.title }}</h3>
                </div>
                <span class="works__cylinder-category">{{ project.category }}</span>
              </div>
              <div v-if="project.link || project.moreInfo" class="works__other-actions">
                <button
                  v-if="project.moreInfo"
                  class="works__other-info-btn"
                  :aria-label="`More info about ${project.title}`"
                  @click.stop="
                    openModal({
                      name: project.title,
                      role: project.category,
                      duration: project.year,
                      description: project.description,
                      tech: project.tech,
                      url: project.link,
                      logo: project.icon,
                      image: project.src || project.image || null,
                    })
                  "
                >
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" stroke-width="1.2" />
                    <path
                      d="M6.5 5.5v4M6.5 4h.01"
                      stroke="currentColor"
                      stroke-width="1.2"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
                <a
                  v-if="project.link"
                  :href="project.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="works__other-link"
                  :aria-label="`Visit ${project.title}`"
                  >↗</a
                >
              </div>
            </div>
            <div v-if="project.progress" class="works__wip-progress">
              <div class="works__wip-progress-bar" :style="{ width: `${project.progress}%` }"></div>
            </div>
          </article>

          <!-- Other Projects Section Header -->
          <div
            class="works__cylinder-item works__cylinder-item--other-header"
            :class="{ 'works__cylinder-item--active': activeIndex === otherDividerIndex }"
            :style="getItemStyle(otherDividerIndex)"
            aria-label="Other projects section"
          >
            <div class="works__building-header-content">
              <h2 class="works__title works__other-section-title">
                <span class="works__title-line">{{ $t('page.works.section.otherLine1') }}</span>
                <span class="works__title-line works__title-line--accent">{{
                  $t('page.works.section.otherLine2')
                }}</span>
              </h2>
            </div>
          </div>

          <!-- Other Projects — individual cylinder items -->
          <article
            v-for="(project, index) in mergedOtherProjects"
            :key="`other-${project.id}`"
            class="works__cylinder-item"
            :class="{
              'works__cylinder-item--active': isOtherActive(index),
              'works__cylinder-item--has-tab': project.caseStudy,
            }"
            :style="getItemStyle(otherDividerIndex + 1 + index)"
            @click="handleOtherClick(project, index)"
          >
            <div v-if="project.caseStudy" class="works__case-study-tab">
              <span class="works__case-study-tab-text">{{ $t('page.works.ui.caseStudy') }}</span>
              <span
                class="works__case-study-tab-loader"
                :aria-label="$t('page.works.ui.comingSoon')"
                :data-tooltip="$t('page.works.ui.comingSoon')"
              >
                <span class="pixel"></span>
                <span class="pixel"></span>
                <span class="pixel"></span>
                <span class="pixel"></span>
                <span class="pixel"></span>
                <span class="pixel"></span>
              </span>
            </div>
            <div class="works__cylinder-item-inner">
              <figure class="works__app-icon">
                <img
                  v-if="project.logo"
                  :src="project.logo"
                  :alt="project.name"
                  class="works__app-icon-img"
                  loading="lazy"
                />
                <span v-else class="works__app-icon-fallback">{{ project.name[0] }}</span>
              </figure>
              <div class="works__item-content">
                <div class="works__item-header">
                  <h3 class="works__cylinder-title">{{ project.name }}</h3>
                </div>
                <span class="works__cylinder-category">{{ project.role }}</span>
              </div>
              <div class="works__other-actions">
                <button
                  class="works__other-info-btn"
                  :aria-label="`More info about ${project.name}`"
                  @click.stop="openModal(project)"
                >
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" stroke-width="1.2" />
                    <path
                      d="M6.5 5.5v4M6.5 4h.01"
                      stroke="currentColor"
                      stroke-width="1.2"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
                <a
                  v-if="project.url"
                  :href="project.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="works__other-link"
                  :aria-label="`Visit ${project.name}`"
                  >↗</a
                >
              </div>
            </div>
          </article>

          <!-- Closing Card -->
          <div
            class="works__cylinder-item works__cylinder-item--closing"
            :class="{ 'works__cylinder-item--active': isClosingActive }"
            :style="getItemStyle(closingIndex)"
            aria-label="Closing message"
          >
            <div class="works__closing-content">
              <h2 class="works__title">
                <span class="works__title-line works__title-line--accent">{{
                  $t('page.works.section.closing')
                }}</span>
              </h2>
            </div>
          </div>
        </div>
      </div>
    </div>

    <OtherProjectModal
      :is-open="!!selectedProject"
      :project="selectedProject || {}"
      @close="closeModal"
    />
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { worksData, otherProjects, inProgressData } from '@/data/worksData'
import OtherProjectModal from '@/components/landingFolio/OtherProjectModal.vue'

gsap.registerPlugin(ScrollTrigger)

const { t, te } = useI18n()

// Translate with fallback to the original value in worksData
const tw = (key, fallback) => (te(`page.works.${key}`) ? t(`page.works.${key}`) : fallback)

const mergedWorksData = computed(() =>
  worksData.map((p) => ({
    ...p,
    category: tw(`projects.${p.id}.category`, p.category),
    description: tw(`projects.${p.id}.description`, p.description),
  }))
)

const mergedInProgressData = computed(() =>
  inProgressData.map((p) => ({
    ...p,
    category: tw(`inProgress.${p.id}.category`, p.category),
    description: tw(`inProgress.${p.id}.description`, p.description),
  }))
)

const mergedOtherProjects = computed(() =>
  otherProjects.map((p) => ({
    ...p,
    role: tw(`otherProjects.${p.id}.role`, p.role),
    description: tw(`otherProjects.${p.id}.description`, p.description),
  }))
)

// ============================================================================
// Constants
// ============================================================================

const SCROLL_DISTANCE_PER_ITEM = 250
const SCROLL_PADDING_END = 80
const SNAP_THRESHOLD = 0.45

// ============================================================================
// Refs & State
// ============================================================================

const sectionRef = ref(null)
const cylinderWrapperRef = ref(null)
const activeIndex = ref(0)
const rotationAngle = ref(0)
const isMobile = ref(false)
const windowHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 800)

let scrollTrigger = null

// ============================================================================
// Responsive cylinder settings
// ============================================================================

const cylinderConfig = computed(() => {
  if (isMobile.value) {
    return {
      radius: 60,
      verticalSpread: Math.max(200, windowHeight.value * 0.34),
    }
  }
  return {
    radius: 160,
    verticalSpread: Math.max(270, windowHeight.value * 0.34),
  }
})

// ============================================================================
// Computed
// ============================================================================

const totalItems = computed(
  () => worksData.length + inProgressData.length + otherProjects.length + 4
) // header + works + building-header + in-progress + other-header + other-projects + closing
const inProgressDividerIndex = computed(() => worksData.length + 1)
const otherDividerIndex = computed(() => worksData.length + inProgressData.length + 2)
const closingIndex = computed(() => otherDividerIndex.value + otherProjects.length + 1)
const anglePerItem = computed(() => 360 / totalItems.value)
const maxRotation = computed(() => -(totalItems.value - 1) * anglePerItem.value)
const scrollDistance = computed(
  () => totalItems.value * SCROLL_DISTANCE_PER_ITEM + SCROLL_PADDING_END
)
const isHeaderActive = computed(() => activeIndex.value === 0)
const selectedProject = ref(null)
const openModal = (project) => {
  selectedProject.value = project
}
const closeModal = () => {
  selectedProject.value = null
}

// ============================================================================
// Helpers
// ============================================================================

const formatIndex = (index) => String(index + 1).padStart(2, '0')
const getPlaceholderIcon = (id) => `https://picsum.photos/seed/${id}/80/80`
const isItemActive = (index) => activeIndex.value === index + 1
const isInProgressActive = (index) => activeIndex.value === inProgressDividerIndex.value + 1 + index
const isOtherActive = (index) => activeIndex.value === otherDividerIndex.value + 1 + index
const isClosingActive = computed(() => activeIndex.value === closingIndex.value)

const getDotArcStyle = (itemIndex) => {
  const t = totalItems.value
  if (t <= 1) return {}
  const progress = itemIndex / (t - 1)
  const angle = progress * Math.PI
  if (isMobile.value) {
    const vRadius = 20
    const offset = -vRadius + vRadius * Math.sin(angle)
    return { transform: `translateY(${offset}px)` }
  }
  const hRadius = 35
  return { transform: `translateX(${-hRadius * Math.sin(angle)}px)` }
}

const getItemStyle = (index) => {
  const relativePosition = index - activeIndex.value

  // Mobile: simplified vertical cylinder — no translateZ to avoid perspective overflow
  if (isMobile.value) {
    // Hide items too far from active to prevent wrap-around at cylinder edges
    if (Math.abs(relativePosition) > 3) {
      return {
        transform: 'translateY(-600px) scale(0.1)',
        opacity: 0,
        zIndex: 0,
        willChange: 'auto',
        backdropFilter: 'none',
        pointerEvents: 'none',
        display: 'none',
      }
    }

    const { radius, verticalSpread } = cylinderConfig.value
    const angle = index * anglePerItem.value + rotationAngle.value
    const radian = (angle * Math.PI) / 180

    const z = Math.cos(radian) * radius
    const y = Math.sin(radian) * verticalSpread
    const normalizedZ = (z + radius) / (2 * radius) // 0 = back, 1 = front

    const opacity = Math.pow(normalizedZ, 6)
    const scale = 0.25 + 0.75 * Math.pow(normalizedZ, 3)
    const nearCenter = Math.abs(relativePosition) <= 1

    const style = {
      transform: `translateY(${y}px) scale(${scale})`,
      opacity,
      zIndex: Math.round(normalizedZ * 100),
      pointerEvents: relativePosition === 0 ? 'auto' : 'none',
    }

    if (nearCenter) {
      style.willChange = 'transform, opacity'
    } else {
      style.willChange = 'auto'
    }
    const blurAmount = Math.round(12 * Math.pow(normalizedZ, 3))
    style.backdropFilter = blurAmount > 0 ? `blur(${blurAmount}px)` : 'none'

    return style
  }

  // Hide items too far behind or too far ahead
  if (relativePosition < -3 || relativePosition > 4) {
    return {
      transform: 'translateZ(-800px) translateY(-600px) scale(0.1)',
      opacity: 0,
      zIndex: 0,
      willChange: 'auto',
      backdropFilter: 'none',
      pointerEvents: 'none',
    }
  }

  const { radius, verticalSpread } = cylinderConfig.value
  const angle = index * anglePerItem.value + rotationAngle.value
  const radian = (angle * Math.PI) / 180

  const z = Math.cos(radian) * radius
  const normalizedZ = (z + radius) / (2 * radius) // 0 = back, 1 = front
  const y = Math.sin(radian) * verticalSpread

  // More gradual fade for a wider cylinder feel
  // pos ±1: normalizedZ ≈ 0.94 → opacity ≈ 0.69, scale ≈ 0.71
  // pos ±2: normalizedZ ≈ 0.78 → opacity ≈ 0.23, scale ≈ 0.44
  // pos ±3: normalizedZ ≈ 0.57 → opacity ≈ 0.03, scale ≈ 0.28
  const opacity = Math.pow(normalizedZ, 10)
  const scale = 0.2 + 0.8 * Math.pow(normalizedZ, 5)
  const nearCenter = Math.abs(relativePosition) <= 1

  // rotateX tilt as items move away from center (cylinder feel)
  const tiltX = angle * 0.6

  const style = {
    transform: `translateZ(${z}px) translateY(${y}px) rotateX(${tiltX}deg) scale(${scale})`,
    opacity,
    zIndex: Math.round(normalizedZ * 100),
  }

  if (nearCenter) {
    style.willChange = 'transform, opacity'
  } else {
    style.willChange = 'auto'
  }
  const blurAmount = Math.round(14 * Math.pow(normalizedZ, 3))
  style.backdropFilter = blurAmount > 0 ? `blur(${blurAmount}px)` : 'none'

  return style
}

const scrollToItem = (index) => {
  if (!scrollTrigger || totalItems.value <= 1) return
  const rotationScrollDistance = totalItems.value * SCROLL_DISTANCE_PER_ITEM
  const totalScrollDistance = rotationScrollDistance + SCROLL_PADDING_END
  const rotationEndProgress = rotationScrollDistance / totalScrollDistance
  const itemProgress = rotationEndProgress / (totalItems.value - 1)
  const targetProgress = index * itemProgress
  const scrollTarget = scrollTrigger.start + targetProgress * totalScrollDistance
  if (window.lenis) {
    window.lenis.scrollTo(scrollTarget)
  } else {
    window.scrollTo({ top: scrollTarget, behavior: 'smooth' })
  }
}

// ============================================================================
// Event Handlers
// ============================================================================

const handleItemClick = (project, index) => {
  const cylinderIndex = index + 1
  if (activeIndex.value !== cylinderIndex) {
    scrollToItem(cylinderIndex)
  }
}

const handleOtherClick = (project, index) => {
  const cylinderIndex = otherDividerIndex.value + 1 + index
  if (activeIndex.value !== cylinderIndex) {
    scrollToItem(cylinderIndex)
  }
}

const handleInProgressClick = (project, index) => {
  const cylinderIndex = inProgressDividerIndex.value + 1 + index
  if (activeIndex.value !== cylinderIndex) {
    scrollToItem(cylinderIndex)
  }
}

const navigateToItem = (index) => {
  if (activeIndex.value === index) return
  scrollToItem(index)
}

// ============================================================================
// Lifecycle
// ============================================================================

const initScrollTrigger = () => {
  if (!sectionRef.value || !cylinderWrapperRef.value) return

  // Calculate the progress point where rotation should complete
  const rotationScrollDistance = totalItems.value * SCROLL_DISTANCE_PER_ITEM
  const totalScrollDistance = rotationScrollDistance + SCROLL_PADDING_END
  const rotationEndProgress = rotationScrollDistance / totalScrollDistance

  scrollTrigger = ScrollTrigger.create({
    trigger: sectionRef.value,
    start: 'top top',
    end: `+=${totalScrollDistance}`,
    scrub: 0.5,
    pin: true,
    pinSpacing: true,
    anticipatePin: 1,
    invalidateOnRefresh: true,
    onUpdate: ({ progress }) => {
      // Clamp progress so rotation completes before the extra padding
      const clampedProgress = Math.min(progress / rotationEndProgress, 1)
      rotationAngle.value = clampedProgress * maxRotation.value

      const newIndex = Math.round(Math.abs(rotationAngle.value) / anglePerItem.value)
      if (newIndex !== activeIndex.value && newIndex < totalItems.value) {
        activeIndex.value = newIndex
      }

      // Track last update time for snap debounce
      lastScrollUpdateTime = performance.now()
    },
  })
}

// ─── Manual snap on scroll-stop (avoids GSAP snap + Lenis conflict) ──────────

let snapDebounceTimer = null
let lastScrollUpdateTime = 0

const checkSnap = () => {
  if (!scrollTrigger) return

  const timeSinceUpdate = performance.now() - lastScrollUpdateTime
  if (timeSinceUpdate < 200) {
    snapDebounceTimer = setTimeout(checkSnap, 100)
    return
  }

  // Scroll has been still for 200ms — check if near a snap point
  const scrollY = window.lenis ? window.lenis.scroll : window.scrollY
  const triggerStart = scrollTrigger.start
  const rotationScrollDistance = totalItems.value * SCROLL_DISTANCE_PER_ITEM
  const totalScrollDistance = rotationScrollDistance + SCROLL_PADDING_END
  const rotationEndProgress = rotationScrollDistance / totalScrollDistance

  const progress = (scrollY - triggerStart) / totalScrollDistance

  if (progress < 0 || progress >= rotationEndProgress) return

  const itemProgress = rotationEndProgress / (totalItems.value - 1)
  const nearestItem = Math.round(progress / itemProgress)

  if (nearestItem <= 0 || nearestItem >= totalItems.value - 1) return

  const snappedProgress = nearestItem * itemProgress
  const distance = Math.abs(progress - snappedProgress)

  if (distance < itemProgress * SNAP_THRESHOLD) {
    const scrollTarget = triggerStart + snappedProgress * totalScrollDistance
    if (window.lenis) {
      window.lenis.scrollTo(scrollTarget, {
        duration: 0.5,
        easing: (t) => 1 - Math.pow(1 - t, 4),
      })
    }
  }
}

const scheduleSnapCheck = () => {
  clearTimeout(snapDebounceTimer)
  snapDebounceTimer = setTimeout(checkSnap, 250)
}

const unregisterSnap = () => {
  clearTimeout(snapDebounceTimer)
  window.removeEventListener('scroll', scheduleSnapCheck)
}

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
  windowHeight.value = window.innerHeight
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  window.addEventListener('scroll', scheduleSnapCheck, { passive: true })
  requestAnimationFrame(initScrollTrigger)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  unregisterSnap()
  scrollTrigger?.kill()
})
</script>

<style scoped>
/* ==========================================================================
   WORKS SECTION - 3D CYLINDER EFFECT
   ========================================================================== */

.works {
  @apply relative;
  min-height: 100vh;
  background: var(--color-deep-black);
  overflow: hidden;
}

/* ==========================================================================
   CONTAINER
   ========================================================================== */

.works__container {
  @apply w-full h-full flex flex-col items-center justify-center;
  position: relative;
  z-index: 1;
  min-height: 100vh;
}

/* ==========================================================================
   PROGRESS NAVIGATION
   ========================================================================== */

.works__progress {
  @apply flex flex-col items-center gap-3;
  position: fixed;
  left: 15rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
}

.works__progress-dot {
  @apply w-2 h-2 rounded-full border-0 p-0;
  background: var(--color-border);
  cursor: pointer;
  transition:
    transform 0.2s ease-out,
    background-color 0.2s ease-out,
    box-shadow 0.2s ease-out,
    width 0.2s ease-out,
    height 0.2s ease-out;
}

.works__progress-dot:hover {
  background: var(--color-text-secondary);
  transform: scale(1.4);
}

.works__progress-dot:focus-visible {
  outline: 2px solid var(--color-electric-purple);
  outline-offset: 2px;
}

.works__progress-dot--active {
  @apply w-3 h-3;
  background: var(--color-electric-purple);
  box-shadow: 0 0 12px var(--color-electric-purple);
}

.works__progress-dot--building.works__progress-dot--active {
  background: var(--color-electric-purple);
  box-shadow: 0 0 12px var(--color-electric-purple);
}

.works__progress-dot--last.works__progress-dot--building.works__progress-dot--active {
  width: 18px;
  height: 2px;
  border-radius: 2px;
  background: var(--color-electric-purple);
  box-shadow: 0 0 8px var(--color-electric-purple);
  opacity: 1;
}

.works__progress-dot--last {
  width: 14px;
  height: 2px;
  border-radius: 2px;
  opacity: 0.4;
}

.works__progress-dot--last.works__progress-dot--active {
  width: 18px;
  height: 2px;
  border-radius: 2px;
  background: var(--color-electric-purple);
  box-shadow: 0 0 8px var(--color-electric-purple);
  opacity: 1;
}

/* ==========================================================================
   HEADER CONTENT
   ========================================================================== */

.works__header-content {
  @apply text-center py-6;
}

.works__header-content .works__title {
  font-family: 'Ms Madi', cursive;
}

.works__header-content .works__title-line {
  @apply font-bold lowercase;
  font-family: 'Ms Madi', cursive;
}

.works__label {
  @apply inline-flex items-center gap-2 text-sm font-medium tracking-widest uppercase mb-6;
  color: var(--color-electric-purple);
}

.works__label-dot {
  @apply w-2 h-2 rounded-full;
  background: var(--color-electric-purple);
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.15);
  }
}

.works__title {
  @apply flex flex-col items-center;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.works__title-line {
  @apply text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold;
  color: var(--color-electric-purple);
}

.works__title-line--accent {
  color: var(--color-text-primary) !important;
}

@keyframes gradient-shift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.works__scroll-hint {
  @apply mt-8 flex justify-center;
}

.works__scroll-hint-icon {
  @apply text-2xl;
  color: var(--color-text-muted);
  animation: bounce-down 1.5s ease-in-out infinite;
}

@keyframes bounce-down {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  50% {
    transform: translateY(8px);
    opacity: 1;
  }
}

/* ==========================================================================
   3D CYLINDER
   ========================================================================== */

.works__cylinder-wrapper {
  width: 100%;
  max-width: 900px;
  height: 60vh;
  min-height: 400px;
  max-height: 500px;
  perspective: 1200px;
  perspective-origin: center center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.works__cylinder {
  @apply relative w-full h-full;
  transform-style: preserve-3d;
  display: flex;
  align-items: center;
  justify-content: center;
}

.works__cylinder-item {
  @apply absolute w-full max-w-3xl;
  padding: 1.5rem 2rem;
  border: 1px solid var(--color-border);
  border-radius: 1rem;
  border-top-left-radius: 0;
  background: var(--color-deep-black) !important;
  /* background: rgba(0, 0, 0, 0.4) !important; */
  backdrop-filter: none;
  cursor: pointer;
  will-change: auto;
  transition:
    border-color 0.25s ease-out,
    background-color 0.25s ease-out,
    box-shadow 0.25s ease-out,
    border-top-left-radius 0.25s ease-out;
}

.works__cylinder-item:hover {
  border-color: var(--color-electric-purple);
}

/* Cards without case-study tab keep rounded top-left */
.works__cylinder-item:not(.works__cylinder-item--has-tab) {
  border-top-left-radius: 1rem;
}

.works__cylinder-item--active {
  border-color: var(--color-electric-purple);
  background: rgba(139, 92, 246, 0.08);
  box-shadow: 0 0 40px rgba(139, 92, 246, 0.15);
}

/* Header Item - Distinct Style (no card look) */
.works__cylinder-item--header {
  background: var(--color-deep-black) !important;
  cursor: default;
  padding: 3rem 4rem;
  border: none;
  box-shadow: 0 0 50px 50px var(--color-deep-black);
  backdrop-filter: none;
}

.works__cylinder-item--header:hover {
  border: none;
  background: transparent;
}

.works__cylinder-item--header.works__cylinder-item--active {
  border: none;
  background: transparent;
}

/* Item Inner */
.works__cylinder-item-inner {
  @apply flex items-center gap-5;
  justify-content: flex-start;
}

/* App Icon */
.works__app-icon {
  @apply flex-shrink-0 m-0;
  width: 60px;
  height: 60px;
  overflow: hidden;
}

.works__app-icon-img {
  @apply w-full h-full object-cover;
}

/* Index */
.works__cylinder-index {
  @apply font-mono text-sm md:text-base;
  color: var(--color-text-muted);
  min-width: 0;
  max-width: 0;
  opacity: 0;
  transform: translateY(10px);
  overflow: hidden;
  transition:
    opacity 0.4s ease-out 0.1s,
    transform 0.4s ease-out 0.1s,
    max-width 0.4s ease-out,
    min-width 0.4s ease-out,
    color 0.25s ease-out;
}

.works__cylinder-item--active .works__cylinder-index {
  color: var(--color-electric-purple);
  opacity: 1;
  transform: translateY(0);
  min-width: 2.5rem;
  max-width: 2.5rem;
}

/* Title */
.works__cylinder-title {
  @apply text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight;
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: var(--color-text-secondary);
  text-align: left;
  flex: 1;
  transition: color 0.4s ease-out;
}

.works__cylinder-item--active .works__cylinder-title {
  color: var(--color-text-primary);
}

/* Category */
.works__cylinder-category {
  @apply uppercase tracking-widest text-xs;
  color: var(--color-text-muted);
  opacity: 0;
  transform: translateY(4px);
  transition:
    opacity 0.4s ease-out 0.15s,
    transform 0.4s ease-out 0.15s,
    color 0.25s ease-out;
}

.works__cylinder-item--active .works__cylinder-category {
  color: var(--color-text-secondary);
  opacity: 1;
  transform: translateY(0);
}

/* Unified item content layout */
.works__item-content {
  @apply flex flex-col flex-1 min-w-0;
  gap: 0.25rem;
}

.works__item-header {
  @apply flex items-center gap-3;
}

/* Case Study folder tab — positioned top-left corner */
.works__case-study-tab {
  position: absolute;
  top: -18px;
  left: -1px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.5rem 0.25rem 0.625rem;
  background: var(--color-deep-black);
  border: 1px solid var(--color-border);
  border-bottom: none;
  font-family: monospace;
  font-size: 0.55rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  line-height: 1;
  white-space: nowrap;
  border-radius: 0.5rem 0.5rem 0 0;
  pointer-events: none;
  transition:
    border-color 0.15s ease-out,
    color 0.15s ease-out;
}

.works__case-study-tab-text {
  color: var(--color-electric-purple);
}

/* Pixel-art loading indicator — 3×2 grid chase animation */
.works__case-study-tab-loader {
  display: grid;
  grid-template-columns: 3px 3px 3px;
  grid-template-rows: 3px 3px;
  gap: 2px;
  width: 13px;
  height: 8px;
  pointer-events: auto;
  cursor: help;
  position: relative;
}

.works__case-study-tab-loader .pixel {
  display: block;
  width: 3px;
  height: 3px;
  background: var(--color-electric-purple);
  opacity: 0.15;
  animation: pixel-bounce 1.4s ease-in-out infinite;
}

/* Chase pattern delays — top row left to right, bottom row right to left */
.works__case-study-tab-loader .pixel:nth-child(1) {
  animation-delay: 0s;
}
.works__case-study-tab-loader .pixel:nth-child(2) {
  animation-delay: 0.12s;
}
.works__case-study-tab-loader .pixel:nth-child(3) {
  animation-delay: 0.24s;
}
.works__case-study-tab-loader .pixel:nth-child(6) {
  animation-delay: 0.36s;
}
.works__case-study-tab-loader .pixel:nth-child(5) {
  animation-delay: 0.48s;
}
.works__case-study-tab-loader .pixel:nth-child(4) {
  animation-delay: 0.6s;
}

@keyframes pixel-bounce {
  0%,
  100% {
    opacity: 0.15;
    transform: scale(1);
  }
  12% {
    opacity: 1;
    transform: scale(1.6);
  }
  24% {
    opacity: 0.6;
    transform: scale(0.8);
  }
  36% {
    opacity: 0.15;
    transform: scale(1);
  }
}

/* Tooltip on hover */
.works__case-study-tab-loader::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  padding: 0.2rem 0.5rem;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  border-radius: 0.3rem;
  color: var(--color-text-primary);
  font-family: system-ui, sans-serif;
  font-size: 0.55rem;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease-out;
}

.works__case-study-tab-loader:hover::after {
  opacity: 1;
}

.works__cylinder-item--active .works__case-study-tab {
  border-color: var(--color-electric-purple);
}

/* ==========================================================================
   RESPONSIVE - TABLET (768px - 1024px)
   ========================================================================== */

@media (max-width: 1024px) {
  .works__cylinder-wrapper {
    max-width: 700px;
    height: 55vh;
    min-height: 350px;
    max-height: 450px;
    perspective: 1000px;
  }

  .works__cylinder-item {
    @apply max-w-2xl;
    padding: 1.25rem 1.75rem;
  }

  .works__cylinder-title {
    @apply text-2xl md:text-3xl lg:text-4xl;
  }
}

/* ==========================================================================
   RESPONSIVE - MOBILE (< 768px) - Vertical Layout
   ========================================================================== */

@media (max-width: 768px) {
  /* Progress - Bottom horizontal arc */
  .works__progress {
    position: fixed;
    left: 50%;
    top: auto;
    bottom: 2.5rem;
    transform: translateX(-50%);
    flex-direction: row;
    align-items: flex-end;
    gap: 0.5rem;
  }

  .works__progress-dot {
    @apply w-2 h-2;
  }

  .works__progress-dot--active {
    @apply w-3 h-3;
  }

  .works__progress-dot--last {
    width: 16px;
    height: 2px;
    border-radius: 2px;
    opacity: 0.4;
  }

  .works__progress-dot--last.works__progress-dot--active {
    width: 20px;
    height: 2px;
    border-radius: 2px;
    background: var(--color-electric-purple);
    box-shadow: 0 0 8px var(--color-electric-purple);
    opacity: 1;
  }

  /* Cylinder — simplified vertical 3D with depth */
  .works__cylinder-wrapper {
    max-width: 100%;
    height: 60vh;
    min-height: 380px;
    max-height: 520px;
    padding: 0 0.5rem;
    overflow: hidden;
  }

  .works__cylinder {
    position: relative;
    width: 100%;
    height: 100%;
  }

  /* Items — no max-width, fill available space */
  .works__cylinder-item {
    padding: 1.25rem 1.5rem;
    border-radius: 0.875rem;
    will-change: auto;
    backdrop-filter: none;
  }

  .works__cylinder-item--has-tab {
    border-top-left-radius: 0;
  }

  .works__cylinder-item--header {
    padding: 2rem;
  }

  /* Vertical layout for item inner */
  .works__cylinder-item-inner {
    @apply flex-col items-center text-center gap-3;
  }

  .works__app-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
  }

  .works__cylinder-title {
    @apply text-xl;
    text-align: center;
  }

  .works__cylinder-category {
    display: block;
    opacity: 1;
    transform: none;
    margin-top: 0.25rem;
  }

  .works__item-header {
    @apply justify-center;
  }

  /* Header content adjustments */
  .works__title-line {
    @apply text-5xl;
  }

  .works__scroll-hint {
    @apply mt-4;
  }

  .works__label {
    @apply mb-4 text-xs;
  }
}

/* ==========================================================================
   RESPONSIVE - SMALL MOBILE (< 480px)
   ========================================================================== */

@media (max-width: 480px) {
  .works__progress {
    bottom: 2rem;
    gap: 0.4rem;
  }

  .works__progress-dot {
    @apply w-1.5 h-1.5;
  }

  .works__progress-dot--active {
    @apply w-2.5 h-2.5;
  }

  .works__progress-dot--last {
    width: 14px;
    height: 2px;
    border-radius: 2px;
    opacity: 0.4;
  }

  .works__progress-dot--last.works__progress-dot--active {
    width: 18px;
    height: 2px;
    border-radius: 2px;
    background: var(--color-electric-purple);
    box-shadow: 0 0 8px var(--color-electric-purple);
    opacity: 1;
  }

  .works__cylinder-wrapper {
    height: 55vh;
    min-height: 320px;
    max-height: 420px;
    padding: 0 0.5rem;
  }

  .works__cylinder-item {
    padding: 1rem 1.25rem;
    border-radius: 0.75rem;
  }

  .works__cylinder-item--has-tab {
    border-top-left-radius: 0;
  }

  .works__cylinder-item--header {
    padding: 1.5rem;
  }

  .works__app-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
  }

  .works__cylinder-title {
    @apply text-lg;
  }

  .works__title-line {
    @apply text-5xl;
  }
}

/* ==========================================================================
   OTHER PROJECTS FOOTER CARD
   ========================================================================== */

.works__cylinder-item--other {
  background: var(--color-deep-black) !important;
  cursor: default;
  padding: 2rem 2.5rem;
  border: none;
  box-shadow: none;
  backdrop-filter: none;
}

.works__cylinder-item--other:hover {
  border: none;
  background: transparent;
}

.works__cylinder-item--other.works__cylinder-item--active {
  border: none;
  background: transparent;
  box-shadow: none;
}

.works__other-content {
  @apply flex flex-col gap-4;
}

.works__other-title {
  margin-bottom: 1rem;
}

/* App icon fallback letter */
.works__app-icon-fallback {
  @apply text-lg font-bold uppercase flex items-center justify-center w-full h-full;
  color: var(--color-electric-purple);
}

.works__other-section-title .works__title-line {
  @apply text-4xl sm:text-5xl md:text-6xl lg:text-7xl;
}

.works__other-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.works__other-item {
  @apply flex items-center gap-3;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--color-border);
  text-decoration: none;
  transition:
    border-color 0.2s ease-out,
    background-color 0.2s ease-out;
  min-width: 0;
}

.works__other-item:hover {
  border-color: var(--color-electric-purple);
  background: rgba(139, 92, 246, 0.06);
}

.works__other-logo {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
}

.works__other-logo-img {
  @apply w-full h-full object-cover;
}

.works__other-logo-fallback {
  @apply text-sm font-bold uppercase;
  color: var(--color-electric-purple);
}

.works__other-info {
  @apply flex flex-col min-w-0 flex-1;
  gap: 0.125rem;
}

.works__other-name {
  @apply text-sm font-semibold truncate;
  color: var(--color-text-primary);
}

.works__other-role {
  @apply text-xs truncate;
  color: var(--color-text-muted);
}

.works__other-actions {
  flex-shrink: 0;
  @apply flex items-center gap-1.5;
}

.works__other-info-btn,
.works__other-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  text-decoration: none;
  font-size: 0.75rem;
  transition:
    border-color 0.2s ease-out,
    color 0.2s ease-out,
    background-color 0.2s ease-out;
}

.works__other-info-btn:hover,
.works__other-link:hover {
  border-color: var(--color-electric-purple);
  color: var(--color-electric-purple);
  background: rgba(139, 92, 246, 0.08);
}

/* Other projects — responsive */
@media (max-width: 768px) {
  .works__cylinder-item--other {
    padding: 1.5rem;
  }

  .works__other-grid {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .works__other-item {
    padding: 0.625rem 0.875rem;
  }
}

@media (max-width: 480px) {
  .works__cylinder-item--other {
    padding: 1.25rem;
  }

  .works__other-logo {
    width: 28px;
    height: 28px;
    border-radius: 6px;
  }
}

/* ==========================================================================
   BUILDING / IN PROGRESS SECTION
   ========================================================================== */

.works__cylinder-item--building-header,
.works__cylinder-item--other-header {
  background: var(--color-deep-black) !important;
  cursor: default;
  padding: 3rem 4rem;
  border: none;
  box-shadow: 0 0 50px 50px var(--color-deep-black);
  backdrop-filter: none;
}

.works__cylinder-item--building-header:hover,
.works__cylinder-item--other-header:hover {
  border: none;
}

.works__cylinder-item--building-header.works__cylinder-item--active,
.works__cylinder-item--other-header.works__cylinder-item--active {
  border: none;
  background: transparent;
}

.works__building-header-content {
  @apply text-center py-6;
}

.works__building-header-content .works__title {
  font-family: 'Ms Madi';
  @apply lowercase;
}

.works__building-header-content .works__title-line {
  @apply font-bold;
  font-family: 'Ms Madi';
}

.works__building-live {
  @apply inline-flex items-center gap-2 text-sm font-medium tracking-widest uppercase mb-6;
  color: var(--color-electric-purple);
}

.works__building-live-dot {
  @apply w-2 h-2 rounded-full flex-shrink-0;
  background: var(--color-electric-purple);
  animation: pulse-dot 1.5s ease-in-out infinite;
}

.works__building-accent {
  background: linear-gradient(
    135deg,
    var(--color-electric-purple) 0%,
    #a78bfa 50%,
    var(--color-electric-purple) 100%
  );
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradient-shift 5s ease infinite;
}

/* WIP Item — dashed border siempre visible */
.works__cylinder-item--wip {
  border-style: dashed;
  border-color: rgba(139, 92, 246, 0.35);
}

.works__cylinder-item--wip:hover {
  border-color: var(--color-electric-purple);
  border-style: solid;
}

.works__cylinder-item--wip.works__cylinder-item--active {
  border-style: solid;
  border-color: var(--color-electric-purple);
  background: rgba(139, 92, 246, 0.08) !important;
  box-shadow: 0 0 40px rgba(139, 92, 246, 0.15);
}

/* Badge siempre visible, más destacado cuando activo */
.works__wip-badge {
  @apply flex items-center gap-1.5 flex-shrink-0;
  padding: 0.25rem 0.625rem;
  border-radius: 100px;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.3);
  opacity: 0.65;
  transition:
    opacity 0.3s ease-out,
    background 0.3s ease-out,
    border-color 0.3s ease-out;
}

.works__cylinder-item--wip.works__cylinder-item--active .works__wip-badge {
  opacity: 1;
  background: rgba(139, 92, 246, 0.16);
  border-color: rgba(139, 92, 246, 0.5);
}

.works__wip-dot {
  @apply w-1.5 h-1.5 rounded-full flex-shrink-0;
  background: var(--color-electric-purple);
  animation: pulse-dot 1.5s ease-in-out infinite;
}

.works__wip-label {
  @apply text-xs font-medium;
  color: var(--color-electric-purple);
}

.works__wip-progress {
  margin-top: 1rem;
  height: 2px;
  background: var(--color-border);
  border-radius: 2px;
  overflow: hidden;
}

.works__wip-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, var(--color-electric-purple), #a78bfa);
  border-radius: 2px;
  transition: width 0.8s ease;
}

@media (max-width: 768px) {
  .works__cylinder-item--building-header {
    padding: 2rem;
  }

  .works__cylinder-item--wip .works__cylinder-item-inner {
    @apply flex-col items-center text-center;
  }

  .works__cylinder-item--wip .works__item-content {
    @apply items-center;
  }

  .works__cylinder-item--wip.works__cylinder-item--active .works__wip-badge {
    transform: none;
  }
}

@media (max-width: 480px) {
  .works__cylinder-item--building-header {
    padding: 1.5rem;
  }
}

/* ==========================================================================
   CLOSING CARD
   ========================================================================== */

.works__cylinder-item--closing {
  background: var(--color-deep-black) !important;
  cursor: default;
  padding: 3rem 4rem;
  border: none;
  box-shadow: 0 0 50px 50px var(--color-deep-black);
  backdrop-filter: none;
}

.works__cylinder-item--closing:hover {
  border: none;
}

.works__cylinder-item--closing.works__cylinder-item--active {
  border: none;
  background: transparent;
}

.works__closing-content {
  @apply text-center py-6;
}

.works__closing-content .works__title {
  font-family: 'Ms Madi', cursive;
}

.works__closing-content .works__title-line {
  @apply font-bold lowercase;
  font-family: 'Ms Madi', cursive;
}

@media (max-width: 768px) {
  .works__cylinder-item--closing {
    padding: 2rem;
  }
}

@media (max-width: 480px) {
  .works__cylinder-item--closing {
    padding: 1.5rem;
  }
}
</style>
